/**
 * Karol Banner Visual Demo — acesso público sem auth.
 * Renderiza o banner em todos os estados para validação UX.
 * NÃO usar em produção — rota /karol-demo apenas para validação interna.
 */
import { Sparkles, ChevronRight } from "lucide-react";

const PRIORITY_STYLES = {
  high:   { label: "Alta",  ring: "ring-amber-500/40", badge: "bg-amber-500/15 text-amber-200" },
  medium: { label: "Média", ring: "ring-slate-500/30", badge: "bg-slate-500/20 text-slate-300" },
  low:    { label: "Baixa", ring: "ring-slate-600/25", badge: "bg-slate-600/20 text-slate-400" },
};

const SCENARIOS = [
  {
    label: "Alta prioridade · agora",
    priority: "high",
    timeLabel: "agora",
    title: "O sistema teve incidentes importantes esta semana",
    description: "Foram registados vários incidentes recentes. Vale verificar WhatsApp, pagamentos e operações antes de o dia avançar.",
  },
  {
    label: "Alta prioridade · há 4 min",
    priority: "high",
    timeLabel: "há 4 min",
    title: "Há pedidos que não chegaram ao fim",
    description: "A Karol registou 7 pedido(s) iniciados mas não concluídos. Vale perceber se há algo a travar no processo de finalização.",
  },
  {
    label: "Alta prioridade · há 1h",
    priority: "high",
    timeLabel: "há 1h",
    title: "O custo de materiais está elevado face à receita",
    description: "Nos últimos 30 dias, o custo de materiais cresceu mais que a receita. Vale rever as compras antes de ajustar o menu.",
  },
  {
    label: "Média prioridade · há 12 min",
    priority: "medium",
    timeLabel: "há 12 min",
    title: "Mais visitantes estão a sair sem finalizar o pedido",
    description: "Hoje houve mais saídas do que pedidos pagos. Pode valer a pena ver o que acontece no momento do checkout.",
  },
  {
    label: "Média prioridade · há 38 min",
    priority: "medium",
    timeLabel: "há 38 min",
    title: "O custo de materiais está a subir gradualmente",
    description: "O custo de materiais aproxima-se de um nível de atenção. Acompanhe as próximas faturas de compra.",
  },
  {
    label: "Baixa prioridade · há 2h",
    priority: "low",
    timeLabel: "há 2h",
    title: "Há clientes habituais de alto valor no restaurante",
    description: "A Karol identificou 5 cliente(s) com pedidos frequentes e valor elevado. Reconhecê-los pode fortalecer a relação com o restaurante.",
  },
  {
    label: "Baixa prioridade · há 3h",
    priority: "low",
    timeLabel: "há 3h",
    title: "A maioria dos pedidos chega por 'WhatsApp'",
    description: "O canal 'WhatsApp' concentra a maior parte dos pedidos. A experiência neste canal tem impacto directo na receita.",
  },
];

function BannerPreview({ priority, timeLabel, title, description }) {
  const style = PRIORITY_STYLES[priority] ?? PRIORITY_STYLES.low;
  return (
    <div
      className={`mx-2 rounded-xl border border-slate-700/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 shadow-lg ring-1 ${style.ring}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-slate-800/80 border border-slate-600/50 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-amber-400/90" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[10px] font-semibold tracking-wide text-slate-500">
                Karol observa
              </span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${style.badge}`}>
                {style.label}
              </span>
              {timeLabel && (
                <span className="text-[10px] text-slate-600">
                  {timeLabel}
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-slate-100 leading-snug line-clamp-2 sm:line-clamp-1">
              {title}
            </p>
            {description && (
              <p className="text-xs text-slate-400 mt-0.5 line-clamp-2 leading-relaxed">
                {description}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-1 shrink-0 text-xs font-medium text-slate-200 bg-slate-800/90 hover:bg-slate-700 border border-slate-600/60 rounded-lg px-3 py-2 transition-colors"
        >
          Ver Karol Central
          <ChevronRight className="w-3.5 h-3.5 opacity-70" />
        </button>
      </div>
    </div>
  );
}

export default function KarolBannerDemoPage() {
  return (
    <div className="min-h-screen bg-slate-950 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-2">
        <div className="mb-8">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1">
            Karol Operational Banner · Visual Demo
          </p>
          <h1 className="text-xl font-semibold text-slate-200">
            Todos os estados — validação UX
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Renderização directa sem API. Para validação interna.
          </p>
        </div>

        {SCENARIOS.map((s) => (
          <div key={s.label} className="space-y-1">
            <p className="text-[10px] text-slate-600 font-mono px-2">{s.label}</p>
            <BannerPreview {...s} />
          </div>
        ))}

        <div className="pt-8">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-3 px-2">
            Estado vazio (sem recommendation)
          </p>
          <div className="mx-2 rounded-xl border border-slate-800/50 bg-slate-900/30 px-4 py-3">
            <p className="text-xs text-slate-700 italic">
              Banner não renderiza — return null quando recommendation é null.
            </p>
          </div>
        </div>

        <div className="pt-8 pb-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-3 px-2">
            Simulação mobile (375px)
          </p>
          <div className="w-[375px] mx-auto space-y-1">
            <p className="text-[10px] text-slate-600 font-mono px-2">Alta · há 4 min · mobile</p>
            <BannerPreview
              priority="high"
              timeLabel="há 4 min"
              title="Há pedidos que não chegaram ao fim"
              description="A Karol registou 7 pedido(s) iniciados mas não concluídos."
            />
          </div>
          <div className="w-[375px] mx-auto space-y-1 mt-2">
            <p className="text-[10px] text-slate-600 font-mono px-2">Média · há 12 min · mobile</p>
            <BannerPreview
              priority="medium"
              timeLabel="há 12 min"
              title="Mais visitantes estão a sair sem finalizar o pedido"
              description="Hoje houve mais saídas do que pedidos pagos."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
